import type { ReactNode } from 'react';

/**
 * Armazón común de las páginas legales **provisorias** (privacidad, términos,
 * preguntas frecuentes).
 *
 * Existe porque ya son tres y comparten lo que importa: un aviso de que el
 * contenido **todavía no es el documento definitivo**. Con el aviso copiado en
 * cada página, alcanza con que alguien edite una para que las otras queden
 * afirmando algo distinto — y acá lo que se afirma es de peso legal.
 *
 * 🔴 **El aviso va ARRIBA, antes del contenido.** Quien llega debe saber que
 * esto no es definitivo **antes** de leerlo, no después de haberlo tomado por
 * bueno.
 *
 * ⚠️ Estas páginas existen para que los enlaces del pie y del banner de cookies
 * lleven a algún lado real. **Un enlace muerto en un aviso legal es peor que la
 * página provisoria**: le niega la información a quien fue a buscarla. Pero
 * tampoco deben publicarse como documento formal — el texto definitivo lo
 * redacta quien corresponda.
 */
interface LegalPlaceholderProps {
  title: string;
  /** Qué es este documento, en una frase, para el recuadro de aviso. */
  notice?: ReactNode;
  children: ReactNode;
}

export function LegalPlaceholder({ title, notice, children }: LegalPlaceholderProps): JSX.Element {
  return (
    <section className="container-fc py-12">
      <div className="mx-auto max-w-2xl">
        <h1 className="font-display text-3xl">{title}</h1>

        <div
          role="note"
          className="mt-6 rounded-card border border-fc-line bg-fc-paper p-4 text-sm text-fc-muted"
        >
          <strong className="text-fc-ink">Documento en preparación.</strong>{' '}
          {notice ?? (
            <>
              El texto definitivo está en elaboración. Lo que sigue es orientativo y todavía no
              reemplaza al documento formal.
            </>
          )}
        </div>

        <div className="mt-8 flex flex-col gap-6 text-fc-ink">{children}</div>
      </div>
    </section>
  );
}

/** Un apartado con título y cuerpo, para usar dentro de `LegalPlaceholder`. */
export function LegalSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div>
      <h2 className="font-display text-xl">{title}</h2>
      <p className="mt-2 text-fc-muted">{children}</p>
    </div>
  );
}
